// Карточка записи «судно × снимок»: позиция АИС на момент съёмки и вся история
// попыток распознавания. Общая оболочка выноски — `map/MapCallout`.

import { useQuery } from '@tanstack/react-query'

import { cabinetApi } from '../api'
import { formatDayTime, formatNumber, useLang, useT } from '../i18n'
import { MapCallout } from '../map/MapCallout'
import { instrumentPoint, statusColor } from './datasetLayer'

interface Props {
  recordId: number
  anchor: { lng: number; lat: number }
  onClose: () => void
}

export function RecordCard({ recordId, anchor, onClose }: Props) {
  const t = useT()
  const { lang } = useLang()
  const q = useQuery({
    queryKey: ['imagery', 'dataset', 'record', recordId],
    queryFn: () => cabinetApi.imagery.dataset.record(recordId),
  })
  const r = q.data?.record
  // Где прибор поставил судно: кольцо и метка стоят на позиции АИС, а не на корпусе.
  const instrument = q.data ? instrumentPoint(q.data.attempts) : null
  const n = (v: number | null | undefined, d = 0) => formatNumber(lang, v ?? null, d)

  return (
    <MapCallout anchor={anchor} width={300} draggable>
      <div className="flex items-start justify-between gap-2" data-callout-handle>
        <div className="text-sm font-semibold text-foam">MMSI {r?.mmsi ?? '…'}</div>
        <button type="button" onClick={onClose} aria-label={t('common.close')}
                className="-mr-1 -mt-1 rounded-md px-2 py-0.5 text-lg leading-none text-mist hover:bg-haze hover:text-foam">
          ×
        </button>
      </div>
      {q.isError && <div className="mt-1 text-[11px] text-buoy">{t('imagery.ds.error')}</div>}
      {r && (
        <div className="mt-1 space-y-0.5 text-[11px] text-mist">
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: statusColor(r.status) }} />
            <span className="text-foam">{t(`imagery.ds.status.${r.status}`)}</span>
            {r.status_reason && <span>· {r.status_reason}</span>}
          </div>
          <div>{t(`imagery.ds.sensor.${r.sensor}`)} · {formatDayTime(lang, r.captured_at)} UTC</div>
          <div className="flex gap-1.5">
            <span className="w-14 shrink-0">{t('imagery.ds.card.ais')}</span>
            <span className="tnum select-all text-foam">{r.lat.toFixed(5)}, {r.lon.toFixed(5)}</span>
          </div>
          {instrument && (
            <div className="flex gap-1.5">
              <span className="w-14 shrink-0">{t('imagery.ds.card.instrument')}</span>
              <span className="tnum select-all text-foam">
                {instrument.lat.toFixed(5)}, {instrument.lon.toFixed(5)}
              </span>
            </div>
          )}
          <div>{t('imagery.ds.card.position', { provider: r.provider, m: n(r.pos_uncertainty_m) })}</div>
          {(r.sog != null || r.cog != null) && (
            <div>{t('imagery.ds.card.motion', { sog: n(r.sog, 1), cog: n(r.cog) })}</div>
          )}
          {r.length_m != null && <div>{t('imagery.ds.card.length', { m: n(r.length_m) })}</div>}
          {r.cloud_pct_local != null && (
            <div>{t('imagery.ds.card.cloud', { pct: n(r.cloud_pct_local) })}</div>
          )}
        </div>
      )}
      {q.data && (
        <div className="mt-2 border-t border-hull pt-1.5">
          <div className="mb-1 text-[11px] text-foam">{t('imagery.ds.card.attempts')}</div>
          {q.data.attempts.length === 0 ? (
            <div className="text-[11px] text-mist">{t('imagery.ds.card.noAttempts')}</div>
          ) : (
            <div className="max-h-[160px] space-y-1 overflow-y-auto text-[11px]">
              {q.data.attempts.map(a => (
                <div key={a.attempt_id} className="rounded-md bg-trench px-1.5 py-1 text-mist">
                  <div className="flex justify-between gap-2">
                    <span className="text-foam">{a.algo_version}</span>
                    <span style={{ color: statusColor(a.outcome) }}>{t(`imagery.ds.status.${a.outcome}`)}</span>
                  </div>
                  <div>
                    {a.nearest_mark_m != null && t('imagery.ds.card.nearest', { m: n(a.nearest_mark_m) })}
                    {a.verdict && ` · ${a.verdict}`}
                    {a.course_delta_deg != null && ` · ${t('imagery.ds.card.courseDelta', { deg: n(a.course_delta_deg) })}`}
                  </div>
                  {a.reason && <div className="text-faint">{a.reason}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </MapCallout>
  )
}
